import React, { useContext, useEffect, useReducer } from 'react';
import { Charts, Text } from '@perch-labs/shrike';
import { copyFor } from 'config';
import { CleanKwh } from 'contexts/clean-kwh';
import { ErrorModule } from 'components/errors';
import { LoadingModule } from 'components/loading';
import { ThemeContext } from 'styled-components';
import { Card } from './usage-graph.style';
import {
  calculateMaxAndMinXAxisDates,
  generateDescription,
  calculateXAxisMonthPosition,
  calculateYAxisMaximum,
  processGraphData,
  generateGraphConfig,
} from './usage-graph.utils';

const getCopy = copyFor('modules.usageGraph');

export const xAxisMonthRange = 7;
export const defaultXAxisMonthPosition = xAxisMonthRange - 1;

const stateReducer = (state, action) => {
  switch (action.type) {
    case 'INIT': {
      let yAxisMax;
      let xAxisMaxAndMin;
      let xAxisMonthPosition;
      const { cleanKwhs, cleanKwhsSinceLatest } = action.payload;
      const graphData = processGraphData(cleanKwhs);
      if (graphData.length) {
        yAxisMax = calculateYAxisMaximum(graphData);
        xAxisMaxAndMin = calculateMaxAndMinXAxisDates({
          cleanKwhsSinceLatest,
          graphData,
          xAxisMonthRange,
        });
        xAxisMonthPosition = calculateXAxisMonthPosition({
          cleanKwhsSinceLatest,
          defaultXAxisMonthPosition,
          graphData,
        });
      }
      return {
        ...state,
        graphData,
        yAxisMax,
        xAxisMaxAndMin,
        xAxisMonthPosition,
      };
    }
    case 'UPDATE': {
      const { cleanKwhsSinceLatest } = action.payload;
      const { graphData } = state;
      const xAxisMaxAndMin = calculateMaxAndMinXAxisDates({
        cleanKwhsSinceLatest,
        graphData,
        xAxisMonthRange,
      });
      const xAxisMonthPosition = calculateXAxisMonthPosition({
        cleanKwhsSinceLatest,
        defaultXAxisMonthPosition,
        graphData,
      });
      return { ...state, xAxisMaxAndMin, xAxisMonthPosition };
    }
    default:
      throw new Error('unknown event type');
  }
};

export const UsageGraph = () => {
  const theme = useContext(ThemeContext);
  const { cleanKwh, cleanKwhs, cleanKwhIndex, connectedWind, loading, error } =
    useContext(CleanKwh);
  const [state, dispatch] = useReducer(stateReducer, {});
  const { graphData, yAxisMax, xAxisMaxAndMin, xAxisMonthPosition } = state;
  const cleanKwhsSinceLatest =
    cleanKwhs && cleanKwhIndex !== undefined
      ? cleanKwhs.length - 1 - cleanKwhIndex
      : 0;

  useEffect(() => {
    if (cleanKwhs?.length)
      dispatch({
        type: 'INIT',
        payload: { cleanKwhs, cleanKwhsSinceLatest },
      });
  }, [cleanKwhs?.[0]?.id]); // initialize graph data for every new plan/cleanKwhs

  useEffect(() => {
    if (graphData)
      dispatch({ type: 'UPDATE', payload: { cleanKwhsSinceLatest } });
  }, [cleanKwhsSinceLatest]);

  if (graphData && !graphData.length) return null;
  if (loading) return <LoadingModule />;
  if (error) return <ErrorModule title={getCopy('title')} />;

  const description = generateDescription({
    connectedWind,
    cleanKwhIndex,
    cleanKwhs,
    cleanKwh,
  });
  const graphConfig = generateGraphConfig({
    theme,
    cleanKwhsSinceLatest,
    cleanKwh,
  });

  return (
    <Card mobileVariant="flat" xAxisLabelChildIndex={xAxisMonthPosition}>
      <Card.Header title={getCopy('title')} />
      <Card.Content>
        <Text margin={{ bottom: '16px' }}>{description}</Text>
        {!!graphData && (
          <Charts.Graph
            aria-hidden={true}
            type="line"
            colors={[theme.colors.alternative1]}
            id="usage-graph"
            config={graphConfig}
            xAxisConfig={{
              labels: {
                hideOverlappingLabels: false,
              },
              ...xAxisMaxAndMin,
            }}
            yAxisConfig={{ max: yAxisMax }}
            series={[
              {
                data: graphData || [],
                name: 'Usage',
              },
            ]}
          />
        )}
      </Card.Content>
    </Card>
  );
};
